import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ReCAPTCHA from 'react-google-recaptcha';
import ForgotPasswordModal from './ForgotPasswordModal';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCapsLock, setShowCapsLock] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleKeyDown = (e) => {
    setShowCapsLock(e.getModifierState && e.getModifierState('CapsLock'));
  };

  const handleKeyUp = (e) => {
    setShowCapsLock(e.getModifierState && e.getModifierState('CapsLock'));
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  // Debug: Log the reCAPTCHA key being used
  useEffect(() => {
    console.log('reCAPTCHA Site Key:', process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LdmwfgrAAAAAETej6n4Wy94RL9ZDznelKqm5fGH");
  }, []);

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check if reCAPTCHA is completed
    if (!recaptchaValue) {
      setError('Please complete the reCAPTCHA verification.');
      setLoading(false);
      return;
    }

    // Check email domain
    const allowedDomain = process.env.REACT_APP_ALLOWED_EMAIL_DOMAIN || 'g.batstate-u.edu.ph';
    const email = formData.email.toLowerCase().trim();
    if (!email.endsWith(`@${allowedDomain}`)) {
      setError('Invalid email address. Please use your institutional email.');
      setLoading(false);
      return;
    }

    const result = await login(formData.email, formData.password, 'employee');
    
    if (result.success) {
      // For employees, navigation will be handled by useEffect
    } else {
      setError('Invalid e-mail or password.');
      // Reset reCAPTCHA on failed login
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaValue(null);
      }
    }
    
    setLoading(false);
  };

  // If already authenticated, redirect away from login
  useEffect(() => {
    if (!authLoading && user && user.role) {
      // All authenticated users go to the dashboard
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate, searchParams]);

  return (
    <div className="employee-login-page">
      <Container className="employee-login-wrapper">
        <div className="login-card shadow">
          <div className="employee-login-split">
            <div className="split-left">
              <div className="left-top-logo">
                <img src="/extensync-logo.png" alt="ExtenSync" />
              </div>
              <div className="left-bottom-logos">
                <img src="/extension-services-logo.png" alt="Extension Services Office" className="circle-logo" />
                <img src="/batstate-u-logo.png" alt="BatState-U" className="circle-logo" />
              </div>
            </div>

            <div className="split-right">
              <div className="right-content">
                <div className="text-center mb-4">
                  <img src="/extensync-logo.png" alt="ExtenSync" className="brand-logo" />
                  <h3 className="mt-3 mb-2 right-title">Sign In</h3>
                </div>

                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email address</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter e-mail address"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      onKeyUp={handleKeyUp}
                      placeholder="Enter your password"
                      required
                    />
                    {showCapsLock && (
                      <Alert variant="warning" className="mt-2 mb-0" style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
                        <i className="bi bi-exclamation-triangle-fill me-1"></i>
                        Caps Lock is on
                      </Alert>
                    )}
                  </Form.Group>

                  <div className="text-end mb-3">
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none"
                      onClick={handleForgotPassword}
                      style={{ fontSize: '0.875rem' }}
                    >
                      Forgot Password?
                    </Button>
                  </div>

                  <div className="mb-4">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LdmwfgrAAAAAETej6n4Wy94RL9ZDznelKqm5fGH"}
                      onChange={handleRecaptchaChange}
                      theme="light"
                    />
                  </div>

                  <Button
                    variant="primary"
                    type="submit"
                    className="employee-login-button d-inline-flex align-items-center justify-content-center w-100"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing in...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-lock-fill me-2"></i>
                        Sign In
                      </>
                    )}
                  </Button>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </Container>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        show={showForgotPassword} 
        onHide={() => setShowForgotPassword(false)} 
      />
    </div>
  );
};

export default Login;
